import { useState, useEffect, useCallback } from "react";
import { LiveLogViewer, eventToLogEntry } from "./LiveLogViewer";
import { StageProgress, parseStageFromProgress } from "./StageProgress";
import { subscribeToJobEvents, getJob, retryJob, type JobEvent, type JobDetail } from "../../client";

type LogEntry = {
  timestamp: string;
  message: string;
  level: string;
  type: string;
  sectionId?: string;
};

type SectionProgress = {
  sectionId: string;
  title: string;
  status: "pending" | "generating" | "rendering" | "complete" | "error";
};

type JobMonitorProps = {
  jobId: string;
  onComplete?: (jobDetail: JobDetail) => void;
  onError?: (error: string) => void;
  onRetry?: (newJobId: string) => void;
};

export function JobMonitor({ jobId, onComplete, onError, onRetry }: JobMonitorProps) {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [currentStage, setCurrentStage] = useState<string | null>(null);
  const [status, setStatus] = useState<string>("connecting");
  const [sections, setSections] = useState<SectionProgress[]>([]);
  const [completedSections, setCompletedSections] = useState(0);
  const [totalSections, setTotalSections] = useState(0);
  const [connected, setConnected] = useState(false);
  const [jobDetail, setJobDetail] = useState<JobDetail | null>(null);
  const [retrying, setRetrying] = useState(false);

  // Load initial job state
  useEffect(() => {
    getJob(jobId)
      .then((detail) => {
        setJobDetail(detail);
        setStatus(detail.status);
        if (detail.progress_stage) {
          setCurrentStage(parseStageFromProgress(detail.progress_stage));
        }
        // Initialize logs from job detail
        if (detail.logs && detail.logs.length > 0) {
          const initialLogs = detail.logs.map((msg, idx) => ({
            timestamp: new Date().toISOString(),
            message: msg,
            level: "info",
            type: "log",
          }));
          setLogs(initialLogs);
        }
      })
      .catch((err) => {
        console.error("Failed to load job:", err);
      });
  }, [jobId]);

  // Subscribe to SSE events
  useEffect(() => {
    const unsubscribe = subscribeToJobEvents(jobId, {
      onEvent: (event: JobEvent) => {
        setLogs((prev) => [...prev.slice(-499), eventToLogEntry(event)]);
      },
      onProgress: (stage, message) => {
        setCurrentStage(parseStageFromProgress(stage));
      },
      onStatus: (newStatus, message) => {
        setStatus(newStatus);
        if (newStatus === "completed" || newStatus === "failed" || newStatus === "cancelled" || newStatus === "interrupted") {
          // Fetch final job state
          getJob(jobId).then((detail) => {
            setJobDetail(detail);
            if (newStatus === "completed") {
              onComplete?.(detail);
            } else if (newStatus === "failed" || newStatus === "interrupted") {
              onError?.(detail.error || (newStatus === "interrupted" ? "Job was interrupted (container restart)" : "Job failed"));
            }
          });
        }
      },
      onSectionStart: (sectionId, title, sectionStatus) => {
        setSections((prev) => {
          const existing = prev.find((s) => s.sectionId === sectionId);
          if (existing) {
            return prev.map((s) =>
              s.sectionId === sectionId ? { ...s, status: sectionStatus as any } : s
            );
          }
          return [...prev, { sectionId, title, status: sectionStatus as any }];
        });
      },
      onSectionProgress: (sectionId, sectionStatus, message) => {
        setSections((prev) =>
          prev.map((s) =>
            s.sectionId === sectionId ? { ...s, status: sectionStatus as any } : s
          )
        );
      },
      onSectionComplete: (sectionId, title, completed, total) => {
        setSections((prev) =>
          prev.map((s) =>
            s.sectionId === sectionId ? { ...s, status: "complete" as const } : s
          )
        );
        setCompletedSections(completed);
        setTotalSections(total);
      },
      onSectionError: (sectionId, error) => {
        setSections((prev) =>
          prev.map((s) => (s.sectionId === sectionId ? { ...s, status: "error" as const } : s))
        );
      },
      onError: (err) => {
        console.error("SSE error:", err);
        setConnected(false);
      },
      onClose: () => {
        setConnected(false);
      },
    });

    setConnected(true);

    return () => {
      unsubscribe();
    };
  }, [jobId, onComplete, onError]);

  const getStatusColor = (s: string): string => {
    switch (s) {
      case "completed":
        return "#66bb6a";
      case "running":
        return "#4a90d9";
      case "failed":
        return "#ef5350";
      case "cancelled":
        return "#ffa726";
      case "interrupted":
        return "#ff9800";  // Orange to indicate recoverable
      default:
        return "#888";
    }
  };

  const handleRetry = async (mode: "resume" | "restart") => {
    setRetrying(true);
    try {
      const result = await retryJob(jobId, { mode });
      onRetry?.(result.job_id);
    } catch (err) {
      console.error("Retry failed:", err);
      setLogs((prev) => [
        ...prev,
        {
          timestamp: new Date().toISOString(),
          message: `Retry failed: ${err}`,
          level: "error",
          type: "log",
        },
      ]);
    } finally {
      setRetrying(false);
    }
  };

  const canRetry = status === "interrupted" || status === "failed";

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", color: "#e0e0e0" }}>
      {/* Header */}
      <div
        style={{
          padding: "0.75rem 1rem",
          borderBottom: "1px solid #333",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#1a1a1a",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 600 }}>Job Monitor</h3>
          <span
            style={{
              padding: "0.25rem 0.5rem",
              backgroundColor: getStatusColor(status),
              borderRadius: "4px",
              fontSize: "0.7rem",
              fontWeight: 500,
              textTransform: "uppercase",
            }}
          >
            {status}
          </span>
          {canRetry && (
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button
                onClick={() => handleRetry("resume")}
                disabled={retrying}
                style={{
                  padding: "0.25rem 0.5rem",
                  backgroundColor: "#4a90d9",
                  border: "none",
                  borderRadius: "4px",
                  color: "white",
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  cursor: retrying ? "not-allowed" : "pointer",
                  opacity: retrying ? 0.6 : 1,
                }}
              >
                {retrying ? "Retrying..." : "Resume"}
              </button>
              <button
                onClick={() => handleRetry("restart")}
                disabled={retrying}
                style={{
                  padding: "0.25rem 0.5rem",
                  backgroundColor: "#666",
                  border: "none",
                  borderRadius: "4px",
                  color: "white",
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  cursor: retrying ? "not-allowed" : "pointer",
                  opacity: retrying ? 0.6 : 1,
                }}
              >
                Restart
              </button>
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <span
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: connected ? "#66bb6a" : "#ef5350",
            }}
          />
          <span style={{ fontSize: "0.7rem", color: "#888" }}>
            {connected ? "Connected" : "Disconnected"}
          </span>
        </div>
      </div>

      {/* Stage Progress */}
      <div style={{ padding: "1rem", borderBottom: "1px solid #333" }}>
        <StageProgress
          currentStage={currentStage}
          sections={sections}
          completedSections={completedSections}
          totalSections={totalSections}
        />
      </div>

      {/* Logs */}
      <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
        <LiveLogViewer logs={logs} autoScroll={true} />
      </div>

      {/* Footer with job info */}
      <div
        style={{
          padding: "0.5rem 1rem",
          borderTop: "1px solid #333",
          backgroundColor: "#1a1a1a",
          fontSize: "0.7rem",
          color: "#888",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <span>Job ID: {jobId}</span>
        {jobDetail?.topic && <span>Topic: {jobDetail.topic}</span>}
        <span>{logs.length} log entries</span>
      </div>
    </div>
  );
}
